import React from 'react';
import { X, Info, Star, Zap, Smartphone, LayoutGrid, Scroll } from 'lucide-react';
import { APP_VERSION } from '../constants';

interface ReleaseNotesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ReleaseNotesModal: React.FC<ReleaseNotesModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[400] flex items-center justify-center p-4 pt-safe pb-safe px-safe">
      <div className="absolute inset-0 bg-slate-950/60 backdrop-blur-md" onClick={onClose}></div>
      <div className="relative bg-white w-full max-w-xl max-h-[85vh] rounded-[3rem] shadow-2xl animate-in zoom-in duration-300 flex flex-col overflow-hidden">
        <header className="p-8 border-b border-gray-100 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="bg-blue-50 p-3 rounded-2xl">
              <Info className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h2 className="font-cinzel font-bold text-xl text-gray-900 leading-tight">Release Notes</h2>
              <p className="text-[10px] font-bold text-blue-600 uppercase tracking-widest">Version {APP_VERSION}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-gray-300 hover:text-gray-500 transition-colors">
            <X className="w-6 h-6" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-8 space-y-8">
          <div className="text-center pb-4 border-b border-gray-50">
            <h3 className="font-cinzel font-bold text-2xl text-gray-900 mb-1">Sponsorship Models & Immersive Play</h3>
            <div className="h-1 w-12 bg-blue-600 mx-auto rounded-full"></div>
          </div>

          {/* Version (24)24 Section */}
          <div className="bg-emerald-50/50 p-6 rounded-[2rem] border border-emerald-100/50 space-y-4">
            <h4 className="font-mono font-bold text-gray-900 flex items-center gap-2">
              <span className="bg-emerald-100 text-emerald-800 text-xs px-2.5 py-1 rounded-full font-bold">Latest in v(24)24</span>
            </h4>
            <div className="space-y-4 pl-2">
              <div>
                <p className="font-bold text-sm text-gray-800 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-600"></span>
                  Android Target SDK 36 & Billing 8.0.0
                </p>
                <p className="text-xs text-gray-600 leading-relaxed pl-3">
                  <strong>Modernized Tooling:</strong> Fully updated to target Android SDK 36, Google Play Billing Library 8.0.0, Gradle 8.13, and AGP 9.0+ with full R8 code shrinking and resource optimization enabled.
                </p>
              </div>
            </div>
          </div>

          {/* Version (23)23 Section */}
          <div className="bg-rose-50/50 p-6 rounded-[2rem] border border-rose-100/50 space-y-4">
            <h4 className="font-mono font-bold text-gray-900 flex items-center gap-2">
              <span className="bg-rose-100 text-rose-800 text-xs px-2.5 py-1 rounded-full font-bold">Previous: v(23)23</span>
            </h4>
            <div className="space-y-4 pl-2">
              <div>
                <p className="font-bold text-sm text-gray-800 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-rose-600"></span>
                  Simplified Support Tiers & Unlock
                </p>
                <p className="text-xs text-gray-600 leading-relaxed pl-3">
                  <strong>Permanent Full Unlock:</strong> Players can now unlock all 100+ current and future puzzles and Faith Duels permanently with a single, flexible contribution tier of $1 to $5.
                </p>
                <p className="text-xs text-gray-600 leading-relaxed pl-3 mt-1">
                  <strong>Supporter Tip Jar:</strong> Added an optional support path for existing players to contribute tips for server upkeep and maintenance, coupled with local currency conversion estimates.
                </p>
              </div>
              <div>
                <p className="font-bold text-sm text-gray-800 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-600"></span>
                  "Running in Chrome" Notice Removal
                </p>
                <p className="text-xs text-gray-600 leading-relaxed pl-3">
                  <strong>Trusted Web Activity (TWA) Harmony:</strong> Updated and aligned fingerprints, metadata schemas, and deep link manifest parameters. This establishes verified digital asset links, removing the browser address bar notice for a pure native look.
                </p>
              </div>
              <div>
                <p className="font-bold text-sm text-gray-800 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-600"></span>
                  Immersive Fullscreen Play
                </p>
                <p className="text-xs text-gray-600 leading-relaxed pl-3">
                  <strong>Safe Area Adaptability:</strong> Refined layouts to expand beautifully across edge-to-edge device edges. The app intelligently pads viewports around notch regions and virtual home buttons for modern notched, foldable, and tablet screens.
                </p>
              </div>
            </div>
          </div>

          {/* Version (15)15 Section */}
          <div className="bg-blue-50/50 p-6 rounded-[2rem] border border-blue-100/50 space-y-4">
            <h4 className="font-mono font-bold text-gray-900 flex items-center gap-2">
              <span className="bg-blue-100 text-blue-800 text-xs px-2.5 py-1 rounded-full font-bold">Previous: v(15)15</span>
            </h4>
            <div className="space-y-4 pl-2">
              <div>
                <p className="font-bold text-sm text-gray-800 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-600"></span>
                  Android 15 Immersive Display
                </p>
                <p className="text-xs text-gray-600 leading-relaxed pl-3">
                  <strong>Edge-to-Edge Support:</strong> Fully optimized PWA and TWA viewports for seamless integration with Android 15. The application dynamically adjusts to translucent status and navigation bars for an immersive scripture experience.
                </p>
              </div>
              <div>
                <p className="font-bold text-sm text-gray-800 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                  Flexible Screen Orientation
                </p>
                <p className="text-xs text-gray-600 leading-relaxed pl-3">
                  <strong>Fluid Adaptability:</strong> Refined responsive rendering to handle clean rotation-resizes, fully supporting portrait, landscape, and foldable layout states across all smartphone and tablet models.
                </p>
              </div>
              <div>
                <p className="font-bold text-sm text-gray-800 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-rose-600"></span>
                  Cryptographic Keystore Alignment
                </p>
                <p className="text-xs text-gray-600 leading-relaxed pl-3">
                  <strong>Secure Signed Builds:</strong> Successfully synchronized our Android production signatures by mapping the trusted legacy release keystore. This resolves package conflict errors and allows seamless app store updates.
                </p>
              </div>
              <div>
                <p className="font-bold text-sm text-gray-800 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-600"></span>
                  PWA & TWA Manifest Integration
                </p>
                <p className="text-xs text-gray-600 leading-relaxed pl-3">
                  <strong>Seamless Installation:</strong> Aligned and verified all metadata parameters to match standard web app standards, fully preserving correct browser shortcuts and offline support across both standard web views and Trusted Web Activities.
                </p>
              </div>
              <div>
                <p className="font-bold text-sm text-gray-800 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-indigo-500"></span>
                  Favicon & Asset Restoration
                </p>
                <p className="text-xs text-gray-600 leading-relaxed pl-3">
                  <strong>Branding Uniformity:</strong> Refined shortcut elements in index.html to dynamically load the high-definition launcher icon as the system browser favicon for professional aesthetic consistency.
                </p>
              </div>
            </div>
          </div>

          {/* Version (11)11 Section */}
          <div className="bg-blue-50/50 p-6 rounded-[2rem] border border-blue-100/50 space-y-4">
            <h4 className="font-mono font-bold text-gray-900 flex items-center gap-2">
              <span className="bg-blue-100 text-blue-800 text-xs px-2.5 py-1 rounded-full font-bold">Previous: v(11)11</span>
            </h4>
            <div className="space-y-3 pl-2">
              <div>
                <p className="font-bold text-sm text-gray-800 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-600"></span>
                  Faith Score Archive & Sharing
                </p>
                <p className="text-xs text-gray-600 leading-relaxed pl-3">
                  <strong>Permanent Visibility:</strong> The Faith Score Archive is now permanently visible in Settings, showing a friendly design for newly installed players before their first duels.
                </p>
                <p className="text-xs text-gray-600 leading-relaxed pl-3 mt-1">
                  <strong>Reformatted Share Actions:</strong> Refined Faith Score Sharing templates and upgraded clipboard export items from "Copy BPQuest" to a direct, human-readable "Copy Code" label.
                </p>
              </div>
              <div>
                <p className="font-bold text-sm text-gray-800 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-600"></span>
                  Login Protocols & Profile Cleanup
                </p>
                <p className="text-xs text-gray-600 leading-relaxed pl-3">
                  <strong>Streamlined Authentication:</strong> Optimized general login protocols and removed the redundant "Account Info" module. Players can now seamlessly customize their identity directly under "My Profile" with customizable names and avatars.
                </p>
              </div>
              <div>
                <p className="font-bold text-sm text-gray-800 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-purple-600"></span>
                  PWA & Service Worker
                </p>
                <p className="text-xs text-gray-600 leading-relaxed pl-3">
                  <strong>Consolidated Offline Loader:</strong> Streamlined the progressive web app offline caching mechanism. Redundant/duplicate sw.js files have been successfully purged, consolidating assets inside `/public/sw.js` for smoother load speeds.
                </p>
              </div>
            </div>
          </div>

          {/* Version (9)9 Section */}
          <div className="bg-blue-50/50 p-6 rounded-[2rem] border border-blue-100/50 space-y-4">
            <h4 className="font-mono font-bold text-gray-900 flex items-center gap-2">
              <span className="bg-blue-100 text-blue-800 text-xs px-2.5 py-1 rounded-full font-bold">Previous: v(9)9</span>
            </h4>
            <div className="space-y-3 pl-2">
              <div>
                <p className="font-bold text-sm text-gray-800 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-600"></span>
                  New
                </p>
                <p className="text-xs text-gray-600 leading-relaxed pl-3">
                  <strong>Share & Export Panel:</strong> Share game stats as formatted copy posts, copy structured rows compatible with Excel/Google Sheets, or download CSV score reports directly on victory.
                </p>
                <p className="text-xs text-gray-600 leading-relaxed pl-3 mt-1">
                  <strong>Score Samples Provisioned:</strong> Pre-compiled sample dataset specimens (.json, .csv, .tsv, .md) are ready for third-party interpreter integration.
                </p>
              </div>
              <div>
                <p className="font-bold text-sm text-gray-800 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-600"></span>
                  Supporter Upgrades
                </p>
                <p className="text-xs text-gray-600 leading-relaxed pl-3">
                  <strong>Local Currency Estimator:</strong> Implemented automatic geographical currency detection and interactive selectors in the Supporter gateway, converting USD fees to local equivalents (EUR, GBP, NGN, INR, CAD, AUD, ZAR, KES, GHS, JPY, BRL).
                </p>
                <p className="text-xs text-gray-600 leading-relaxed pl-3 mt-1">
                  <strong>Sandboxed Simulated Billing:</strong> Fully simulated payment authorizations to securely grant immediate "Supporter" level access.
                </p>
              </div>
            </div>
          </div>

          {/* Version (8)8 Section */}
          <div className="bg-blue-50/50 p-6 rounded-[2rem] border border-blue-100/50 space-y-4">
            <h4 className="font-mono font-bold text-gray-900 flex items-center gap-2">
              <span className="bg-blue-100 text-blue-800 text-xs px-2.5 py-1 rounded-full font-bold">Previous: v(8)8</span>
            </h4>
            <div className="space-y-3 pl-2">
              <div>
                <p className="font-bold text-sm text-gray-800 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-600"></span>
                  New
                </p>
                <p className="text-xs text-gray-600 leading-relaxed pl-3">Added a Share App button in Settings!</p>
              </div>
              <div>
                <p className="font-bold text-sm text-gray-800 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                  Security
                </p>
                <p className="text-xs text-gray-600 leading-relaxed pl-3">Added a parent gate for Google Pay billing.</p>
              </div>
              <div>
                <p className="font-bold text-sm text-gray-800 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-600"></span>
                  Fixes
                </p>
                <p className="text-xs text-gray-600 leading-relaxed pl-3">Restored verse citations to winVerses; fixed version displays and corrected the puzzle congrats page loop in Faith Duel links.</p>
              </div>
            </div>
          </div>

          <section className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="bg-emerald-50 p-2 rounded-xl">
                <Scroll className="w-4 h-4 text-emerald-600" />
              </div>
              <h4 className="font-bold text-gray-900">A Growing Library</h4>
            </div>
            <div className="space-y-3 pl-10">
              <div>
                <p className="font-bold text-sm text-gray-800">101 Total Puzzles</p>
                <p className="text-xs text-gray-500 leading-relaxed">We have added 51 brand-new puzzles, doubling the library! You now have a full "centennial" collection of 101 unique challenges to solve.</p>
              </div>
              <div>
                <p className="font-bold text-sm text-gray-800">The Story Behind the Art</p>
                <p className="text-xs text-gray-500 leading-relaxed">Deepen your connection to the Word. Every single puzzle now features a Bible Story—choose to read or listen to the scripture and history that inspired the artwork.</p>
              </div>
            </div>
          </section>

          <section className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="bg-blue-50 p-2 rounded-xl">
                <LayoutGrid className="w-4 h-4 text-blue-600" />
              </div>
              <h4 className="font-bold text-gray-900">Enhanced Visuals</h4>
            </div>
            <div className="space-y-3 pl-10">
              <div>
                <p className="font-bold text-sm text-gray-800">Modern Look</p>
                <p className="text-xs text-gray-500 leading-relaxed">We’ve refreshed all category icons with a clean, modern aesthetic for easier navigation.</p>
              </div>
              <div>
                <p className="font-bold text-sm text-gray-800">Full-Screen Previews</p>
                <p className="text-xs text-gray-500 leading-relaxed">Use the new Image Modal to view the beautiful puzzle artwork in high definition both before you start and after you solve it.</p>
              </div>
            </div>
          </section>

          <section className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="bg-rose-50 p-2 rounded-xl">
                <Smartphone className="w-4 h-4 text-rose-600" />
              </div>
              <h4 className="font-bold text-gray-900">System & Performance</h4>
            </div>
            <div className="space-y-3 pl-10">
              <div>
                <p className="font-bold text-sm text-gray-800">Android 15 Optimized</p>
                <p className="text-xs text-gray-500 leading-relaxed">The app is now fully compliant with the latest SDK 35 standards. Enjoy a seamless edge-to-edge display that makes the most of your screen.</p>
              </div>
              <div>
                <p className="font-bold text-sm text-gray-800">Flexible Orientation</p>
                <p className="text-xs text-gray-500 leading-relaxed">Whether you’re on a phone, tablet, or foldable, the app now supports all orientations and screen resizing for a smoother experience.</p>
              </div>
            </div>
          </section>
        </div>

        <footer className="p-6 bg-gray-50 text-center shrink-0">
          <button 
            onClick={onClose}
            className="w-full max-w-sm bg-gray-900 text-white py-4 rounded-2xl font-bold hover:bg-gray-800 transition active:scale-95 shadow-lg"
          >
            Great, Let's Play!
          </button>
        </footer>
      </div>
    </div>
  );
};

export default ReleaseNotesModal;
